import { Button, ButtonProps, CircularProgress } from "@mui/material";

export interface SaveButtonProps {
  onClick: () => void;
  disabled?: boolean;
  isSaving?: boolean;
  saveText?: string;
  savingText?: string;
  variant?: "text" | "outlined" | "contained";
  color?: ButtonProps["color"];
  size?: ButtonProps["size"];
  fullWidth?: boolean;
  sx?: ButtonProps["sx"];
  className?: string;
  type?: "button" | "submit" | "reset";
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

/**
 * A reusable save button component that displays a loading indicator when saving.
 *
 * @param onClick - Handler called when the button is clicked
 * @param disabled - Whether the button is disabled
 * @param isSaving - Whether the save operation is in progress
 * @param saveText - Text to display when not saving (default: "SAVE")
 * @param savingText - Text to display when saving (default: derived from saveText)
 * @param variant - MUI button variant (default: "text")
 * @param color - MUI button color (default: from theme)
 * @param size - MUI button size (default: "medium")
 * @param fullWidth - Whether the button should take full width
 * @param sx - MUI sx prop for custom styling
 * @param className - Additional CSS class names
 * @param type - HTML button type (default: "button")
 * @param startIcon - Icon to display at the start (overridden when isSaving)
 * @param endIcon - Icon to display at the end
 */
export function SaveButton({
  onClick,
  disabled = false,
  isSaving = false,
  saveText = "SAVE",
  savingText,
  variant = "text",
  color,
  size,
  fullWidth,
  sx,
  className,
  type = "button",
  startIcon,
  endIcon,
}: SaveButtonProps) {
  // Create savingText if not provided by converting saveText to progressive form
  const computedSavingText =
    savingText ||
    (() => {
      // Mapping of base verbs to their progressive (-ing) forms
      const verbMap: Record<string, string> = {
        allocate: "Allocating",
        save: "Saving",
        submit: "Submitting",
        create: "Creating",
        update: "Updating",
        delete: "Deleting",
      };

      const text = saveText.toLowerCase();

      // Check if the text matches any known verb pattern
      for (const [baseForm, ingForm] of Object.entries(verbMap)) {
        if (text.endsWith(baseForm)) {
          const prefix = saveText.slice(0, saveText.length - baseForm.length);
          return `${prefix}${ingForm}...`;
        }
      }
      // Default: remove trailing 'e' if present and append "ing..."
      if (text.endsWith("e")) {
        return `${saveText.slice(0, -1)}ing...`;
      }

      // Fallback: just append ellipsis
      return `${saveText}...`;
    })();

  return (
    <Button
      onClick={onClick}
      variant={variant}
      color={color}
      size={size}
      fullWidth={fullWidth}
      sx={sx}
      className={className}
      type={type}
      disabled={disabled || isSaving}
      startIcon={isSaving ? <CircularProgress size={16} /> : startIcon}
      endIcon={endIcon}
    >
      {isSaving ? computedSavingText : saveText}
    </Button>
  );
}
